import React from 'react';
import { useNavigate } from 'react-router-dom';

export const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-neutral-white dark:bg-neutral-black">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/policies')}
            className="text-brand-teal hover:text-brand-teal-hover flex items-center mb-4"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Policies
          </button>
          <h1 className="text-4xl font-bold text-neutral-black dark:text-neutral-white mb-2">
            Privacy Policy
          </h1>
          <p className="text-sm text-neutral-medium-gray dark:text-neutral-medium-gray">
            Last Updated: November 9, 2025
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-neutral dark:prose-invert max-w-none">
          <div className="space-y-8 text-neutral-black dark:text-neutral-white">

            <section>
              <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
              <p className="mb-4">
                Horwath HTL ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains
                how we collect, use, disclose, and safeguard your information when you use ChatHTL ("Service"). This
                policy complies with the General Data Protection Regulation (GDPR), California Consumer Privacy Act (CCPA),
                and other applicable privacy laws.
              </p>
              <p className="mb-4">
                <strong>Data Controller:</strong> Horwath HTL is the data controller responsible for your personal information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>

              <h3 className="text-xl font-semibold mb-3 mt-6">2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Account Information:</strong> Email address, name, and authentication credentials</li>
                <li><strong>Payment Information:</strong> Payment card details processed securely through Stripe (we do not store full card numbers)</li>
                <li><strong>Chat Content:</strong> Messages, queries, and conversations with our AI assistant</li>
                <li><strong>Profile Information:</strong> Any additional information you choose to provide</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">2.2 Information Collected Automatically</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Usage Data:</strong> Pages viewed, features used, time spent on the Service, and interaction patterns</li>
                <li><strong>Device Information:</strong> IP address, browser type, operating system, device type, and unique device identifiers</li>
                <li><strong>Cookies and Tracking:</strong> Session tokens, preferences, and analytics data (see Section 8)</li>
                <li><strong>Log Data:</strong> Server logs including timestamps, request/response data, and error logs</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">2.3 Information from Third Parties</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Authentication Providers:</strong> Information from email-based authentication systems</li>
                <li><strong>Payment Processors:</strong> Transaction status and payment confirmation from Stripe</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
              <p className="mb-4">We use your information for the following purposes:</p>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.1 Service Provision</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Providing and maintaining the ChatHTL service</li>
                <li>Processing your queries and generating AI responses</li>
                <li>Managing your account and authentication</li>
                <li>Processing payments and subscriptions</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.2 Service Improvement</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Analyzing usage patterns to improve the Service</li>
                <li>Training and improving our AI models (using anonymized data)</li>
                <li>Developing new features and functionality</li>
                <li>Conducting research and analytics</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.3 Communication</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Sending service-related notifications and updates</li>
                <li>Responding to your inquiries and support requests</li>
                <li>Sending marketing communications (with your consent, where required)</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">3.4 Legal and Security</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Complying with legal obligations</li>
                <li>Protecting against fraud, abuse, and security threats</li>
                <li>Enforcing our Terms of Use</li>
                <li>Resolving disputes and defending legal claims</li>
              </ul>

              <p className="mb-4 mt-6">
                <strong>Legal Basis for Processing (GDPR):</strong> We process your personal data based on: (a) your consent;
                (b) performance of our contract with you; (c) our legitimate interests; or (d) compliance with legal obligations.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">4. Data Sharing and Disclosure</h2>
              <p className="mb-4">We may share your information in the following circumstances:</p>

              <h3 className="text-xl font-semibold mb-3 mt-6">4.1 Service Providers</h3>
              <p className="mb-4">
                We share information with third-party service providers who perform services on our behalf:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Cloud Infrastructure:</strong> Microsoft Azure (data hosting and processing)</li>
                <li><strong>Payment Processing:</strong> Stripe (payment transactions)</li>
                <li><strong>AI Services:</strong> OpenAI and Anthropic (AI model access)</li>
                <li><strong>Analytics:</strong> Usage analytics and monitoring services</li>
              </ul>
              <p className="mb-4">
                These providers are contractually obligated to protect your data and use it only for the specified purposes.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">4.2 Legal Requirements</h3>
              <p className="mb-4">We may disclose information when required by law or to:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Comply with legal processes, court orders, or government requests</li>
                <li>Protect our rights, property, or safety</li>
                <li>Prevent fraud or security threats</li>
                <li>Enforce our Terms of Use</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">4.3 Business Transfers</h3>
              <p className="mb-4">
                In the event of a merger, acquisition, or sale of assets, your information may be transferred to the
                acquiring entity, subject to the same privacy protections.
              </p>

              <h3 className="text-xl font-semibold mb-3 mt-6">4.4 With Your Consent</h3>
              <p className="mb-4">
                We may share information for other purposes with your explicit consent.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">5. International Data Transfers</h2>
              <p className="mb-4">
                Your information may be transferred to and processed in countries other than your country of residence,
                including the United States. These countries may have different data protection laws.
              </p>
              <p className="mb-4">
                <strong>GDPR Compliance:</strong> For transfers from the European Economic Area (EEA), we rely on:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li>Standard Contractual Clauses approved by the European Commission</li>
                <li>Adequacy decisions where applicable</li>
                <li>Other lawful transfer mechanisms</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">6. Data Retention</h2>
              <p className="mb-4">
                We retain your personal information for as long as necessary to fulfill the purposes described in this
                Privacy Policy, unless a longer retention period is required by law.
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Account Data:</strong> Retained while your account is active and for a reasonable period after deletion</li>
                <li><strong>Chat History:</strong> Retained for the duration of your subscription or until you request deletion</li>
                <li><strong>Payment Records:</strong> Retained as required by financial regulations (typically 7 years)</li>
                <li><strong>Usage Logs:</strong> Retained for up to 12 months for security and analytics purposes</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">7. Data Security</h2>
              <p className="mb-4">
                We implement appropriate technical and organizational measures to protect your information:
              </p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Encryption:</strong> Data in transit (TLS/SSL) and at rest (AES-256)</li>
                <li><strong>Access Controls:</strong> Role-based access and authentication requirements</li>
                <li><strong>Monitoring:</strong> Continuous security monitoring and threat detection</li>
                <li><strong>Regular Audits:</strong> Security assessments and vulnerability testing</li>
                <li><strong>Incident Response:</strong> Procedures for detecting and responding to data breaches</li>
              </ul>
              <p className="mb-4">
                While we strive to protect your information, no security system is impenetrable. We cannot guarantee
                absolute security of your data.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">8. Cookies and Tracking Technologies</h2>
              <p className="mb-4">We use cookies and similar technologies to:</p>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Essential Cookies:</strong> Required for authentication and core functionality</li>
                <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
                <li><strong>Analytics Cookies:</strong> Understand how you use the Service</li>
              </ul>
              <p className="mb-4">
                You can control cookies through your browser settings. Disabling certain cookies may limit functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">9. Your Privacy Rights</h2>
              <p className="mb-4">Depending on your location, you may have the following rights:</p>

              <h3 className="text-xl font-semibold mb-3 mt-6">9.1 GDPR Rights (EEA, UK, Switzerland)</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Right to Access:</strong> Request copies of your personal data</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
                <li><strong>Right to Restriction:</strong> Limit how we use your data</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for processing at any time</li>
                <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your local data protection authority</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">9.2 CCPA Rights (California Residents)</h3>
              <ul className="list-disc pl-6 mb-4 space-y-2">
                <li><strong>Right to Know:</strong> Request information about data collection and use</li>
                <li><strong>Right to Delete:</strong> Request deletion of personal information</li>
                <li><strong>Right to Opt-Out:</strong> Opt-out of sale of personal information (we do not sell personal information)</li>
                <li><strong>Right to Non-Discrimination:</strong> Equal service regardless of privacy rights exercise</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">9.3 Exercising Your Rights</h3>
              <p className="mb-4">
                To exercise your rights, contact us at <a href="mailto:privacy@chathtl.com" className="text-brand-teal hover:text-brand-teal-hover">privacy@chathtl.com</a>.
                We will respond to verified requests within the timeframes required by applicable law (typically 30 days).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">10. Children's Privacy</h2>
              <p className="mb-4">
                The Service is not intended for children under 16 years of age. We do not knowingly collect personal
                information from children under 16. If you become aware that a child has provided us with personal
                information, please contact us, and we will take steps to delete such information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">11. Changes to This Privacy Policy</h2>
              <p className="mb-4">
                We may update this Privacy Policy from time to time. We will notify you of material changes by posting
                the updated policy on this page and updating the "Last Updated" date. For significant changes, we may
                provide additional notice (such as email notification).
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">12. Contact Information</h2>
              <p className="mb-4">
                If you have questions about this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <p className="mb-4">
                <strong>Email:</strong> <a href="mailto:privacy@chathtl.com" className="text-brand-teal hover:text-brand-teal-hover">privacy@chathtl.com</a><br />
                <strong>Support:</strong> <a href="mailto:support@chathtl.com" className="text-brand-teal hover:text-brand-teal-hover">support@chathtl.com</a>
              </p>
              <p className="mb-4">
                <strong>Data Protection Officer (DPO):</strong> For GDPR-related inquiries, contact our DPO at{' '}
                <a href="mailto:dpo@chathtl.com" className="text-brand-teal hover:text-brand-teal-hover">dpo@chathtl.com</a>
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};
